'use client'

import type { ConditionalNumberField } from './type'
import type { Control, FieldErrorsImpl, FieldValues } from 'react-hook-form'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select as SelectComponent,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import React, { useState } from 'react'
import { Controller } from 'react-hook-form'

import { Error } from '../Error'
import { Width } from '../Width'
import { TypedLocale } from 'payload'

export const ConditionalNumber: React.FC<
  ConditionalNumberField & {
    control: Control<FieldValues, any>
    errors: Partial<FieldErrorsImpl<{ [x: string]: any }>>
    locale: TypedLocale
  }
> = ({
  name,
  control,
  errors,
  label,
  required,
  width,
  defaultValue = 0,
  textInputLabel = 'Please specify',
  textInputPlaceholder = 'Please specify...',
  textInputRequired = true,
  locale,
}) => {
  const [numberValue, setNumberValue] = useState(defaultValue || 0)

  const textInputName = `${name}_text`

  return (
    <Width width={width}>
      <Label htmlFor={name}>
        {label}
        {required && (
          <span className="required">
            * <span className="sr-only">(required)</span>
          </span>
        )}
      </Label>

      <Controller
        control={control}
        defaultValue={defaultValue}
        name={name}
        render={({ field: { onChange, value } }) => (
          <SelectComponent
            onValueChange={(val) => {
              const newValue = parseInt(val) || 0
              onChange(newValue)
              setNumberValue(newValue)
            }}
            value={String(value || 0)}
            defaultValue={String(defaultValue || 0)}
          >
            <SelectTrigger className="mb-4 w-full" id={name}>
              <SelectValue placeholder={label} />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 8 }, (_, i) => i).map((num) => (
                <SelectItem key={num} value={String(num)}>
                  {num}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectComponent>
        )}
        rules={{ required }}
      />
      {errors[name] && <Error name={name} locale={locale} />}

      {/* Conditional Text Input */}
      {numberValue !== 0 && (
        <div className="mt-4">
          <Label htmlFor={textInputName}>
            {textInputLabel}
            {textInputRequired && (
              <span className="required">
                * <span className="sr-only">(required)</span>
              </span>
            )}
          </Label>
          <Controller
            control={control}
            name={textInputName}
            render={({ field }) => (
              <Input
                {...field}
                id={textInputName}
                type="text"
                className="mt-2"
                placeholder={textInputPlaceholder}
              />
            )}
            rules={{ required: textInputRequired }}
          />
          {errors[textInputName] && <Error name={textInputName} locale={locale} />}
        </div>
      )}
    </Width>
  )
}
